import { useQuery } from '@tanstack/react-query';
import { HiringOpenings, SourcingService } from 'api-clients/monolith';
import _range from 'lodash/range';
import { PropsWithChildren, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useHiringGoalIdParam } from '@/hooks/use-hiring-goal-id-param';
import { queryKeys } from '@/query-keys';

interface HiringOpeningsHoverCardProps extends PropsWithChildren {
  openingSlug: string;
}

export function HiringOpeningsHoverCard({
  children,
  openingSlug,
}: HiringOpeningsHoverCardProps) {
  const [open, setOpen] = useState(false);
  const { hiringGoalId } = useHiringGoalIdParam();

  const { data, isFetching } = useQuery({
    queryKey: [
      ...queryKeys.dashboard.hiringOpenings,
      openingSlug,
      hiringGoalId,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpeningsHiringOpenings(
        openingSlug,
        hiringGoalId,
      ),
    enabled: open,
  });

  const hiringOpenings = useMemo(() => data?.hiring_openings ?? [], [data]);

  return (
    <HoverCard open={open} onOpenChange={setOpen}>
      <HoverCardTrigger>{children}</HoverCardTrigger>
      <HoverCardContent align="start" className="w-80">
        <div className="flex flex-col items-start justify-center gap-2 py-1.5">
          <Typography variant="small1" className="!text-fx-neutral-600">
            <FormattedMessage
              defaultMessage="This number is the aggregate of multiple openings within the hiring workflow. The breakdown is below."
              description="Intro for per-opening hire counts when goals are linked"
            />
          </Typography>

          <div className="flex w-full items-center justify-between py-1.5">
            <Typography variant="caption">
              <FormattedMessage
                defaultMessage="Openings"
                description="Hover table column: job title in the breakdown"
              />
            </Typography>
            <Typography variant="caption">
              <FormattedMessage
                defaultMessage="Hires"
                description="Hover card: hires count for a linked opening"
              />
            </Typography>
          </div>

          <HiringOpeningsRows
            hiringOpenings={hiringOpenings}
            isFetching={isFetching}
          />
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

function HiringOpeningsRows({
  hiringOpenings,
  isFetching,
}: {
  hiringOpenings: HiringOpenings[];
  isFetching: boolean;
}) {
  if (isFetching)
    return (
      <>
        {_range(2).map(index => (
          <div
            key={index}
            className="flex w-full items-center justify-between py-1.5"
          >
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-5" />
          </div>
        ))}
      </>
    );

  return (
    <>
      {hiringOpenings.map(opening => (
        <div
          key={opening.external_id}
          className="flex w-full items-center justify-between py-1.5"
        >
          <Typography variant="body1" className="!text-fx-neutral-600 w-[90%]">
            {opening.title}
          </Typography>
          <Typography variant="body2" className="!text-fx-neutral-600">
            {opening.hire_count}
          </Typography>
        </div>
      ))}
    </>
  );
}
